import React from "react";
import { motion } from "framer-motion";
import { ChatAlt2Icon } from "@heroicons/react/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp, faTwitter, faLinkedin, faGithub } from "@fortawesome/free-brands-svg-icons";

const WHATSAPP_NUMBER = "+2348030914767";

const quickMessages = [
  { label: "👋 Say hello", text: "Hi Codehouze! I'd love to connect." },
  { label: "💼 Start a project", text: "Hi Codehouze, I have a project I'd like to discuss with your team." },
  { label: "🤝 Partner with us", text: "Hi Codehouze, I'm interested in a partnership or collaboration." },
  { label: "❓ Ask a question", text: "Hi Codehouze, I have a question about your services." },
];

const socialLinks = [
  { href: "https://twitter.com/codehouze", icon: faTwitter, label: "Twitter", color: "hover:text-blue-400" },
  { href: "https://linkedin.com/in/codehouze", icon: faLinkedin, label: "LinkedIn", color: "hover:text-blue-500" },
  { href: "https://www.github.com/codehouze/", icon: faGithub, label: "GitHub", color: "hover:text-white" },
];

const buildWhatsAppUrl = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

const CodehouzeContact = () => {
  return (
    <section id="contact" className="py-20 bg-gray-900/50">
      <div className="mx-auto max-w-6xl px-6">
        <div className="flex flex-col items-center mb-16">
          <ChatAlt2Icon className="w-8 h-8 text-codehouzeOrange mb-4" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white">Let's Talk</h2>
          <div className="codehouze-divider" />
        </div>

        <div className="flex flex-col md:flex-row gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="flex-1"
          >
            <h3 className="text-3xl font-bold text-white mb-4">Let's Build Together 🚀</h3>
            <p className="text-gray-400 text-lg leading-relaxed mb-8">
              Have a project in mind, or just want to explore what's possible?
              Send us a message and we'll get back to you on WhatsApp.
            </p>

            <div className="flex items-center gap-5">
              {socialLinks.map(({ href, icon, label, color }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className={`text-gray-500 ${color} transition-colors duration-200`}
                >
                  <FontAwesomeIcon icon={icon} className="text-2xl" />
                </a>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="flex-1 w-full relative"
          >
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-codehouzeMaroon/60 via-codehouzeOrange/40 to-transparent opacity-60 blur-sm" />
            <div className="relative bg-gray-900 border border-white/10 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 bg-[#075E54] px-5 py-4">
                <div className="w-10 h-10 rounded-full bg-codehouzeOrange/20 flex items-center justify-center">
                  <FontAwesomeIcon icon={faWhatsapp} className="text-white text-xl" />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm">Codehouze Technology Solutions</p>
                  <p className="text-emerald-200 text-xs">Typically replies within a day</p>
                </div>
                <span className="ml-auto w-2.5 h-2.5 rounded-full bg-codehouzeOrange animate-pulse" />
              </div>

              <div className="p-5 space-y-3">
                <div className="bg-gray-700/60 rounded-xl rounded-tl-none px-4 py-3 max-w-xs">
                  <p className="text-gray-200 text-sm">
                    👋 Hey! Pick a quick message below or tap the button to start a custom chat.
                  </p>
                  <p className="text-gray-500 text-xs mt-1 text-right">now</p>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                  {quickMessages.map(({ label, text }) => (
                    <a
                      key={label}
                      href={buildWhatsAppUrl(text)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm bg-gray-700 hover:bg-[#075E54] border border-gray-600 hover:border-[#075E54] text-gray-300 hover:text-white px-3 py-1.5 rounded-full transition-all duration-200"
                    >
                      {label}
                    </a>
                  ))}
                </div>
              </div>

              <div className="px-5 pb-5">
                <a
                  href={buildWhatsAppUrl("Hi Codehouze, I'd like to get in touch!")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-3 w-full bg-[#25D366] hover:bg-[#20b858] text-white font-semibold py-3 rounded-xl transition-colors duration-200"
                >
                  <FontAwesomeIcon icon={faWhatsapp} className="text-xl" />
                  Open WhatsApp Chat
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default CodehouzeContact;
